import { useState, type ReactNode } from 'react';
import { Alert, Button, Card, Input, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  cancelScheduledTariff,
  getTariff,
  scheduleTariffChange,
  type PAScheduledTariff,
  type PATariff,
  type PATariffOption,
} from '../api/tariff';
import type { PAAccount, PAContract } from '../api/money';
import { formatCivilDate } from '../format';
import { AccountScope } from '../money/AccountScope';
import { DataTable, FactList, type Column } from '../money/DataTable';
import { Money } from '../money/Money';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';
import { billingModelLabel } from '../money/labels';
import { errorText } from '../session/errorText';
import { ApiError } from '../api/client';

/**
 * ============================================================================
 * «ТАРИФНЫЙ ПЛАН» — §7a/7b/7c/7d, F16, Appendix L §2.12, T2(a)
 * ============================================================================
 *
 * # ⚠ THE DATE IS THE WHOLE ITEM, AND THE SCREEN PRINTS THE SERVER'S
 *
 * A subscriber may pick a day; the server SNAPS IT FORWARD to the next billing-cycle
 * boundary and answers with `effective_date` — the day that was actually written. **This
 * screen renders that echo and never the date he typed.** The reason is the complaint the
 * item exists to prevent: the engine changes the tariff's DISPLAY at the assignment's own
 * date and the MONEY at the boundary, so a portal that confirmed «сменим 20 августа» would
 * have him telephoning on the 20th about a charge that is still the old one. The server
 * removed the divergence by writing the assignment AT the boundary; the screen's only job is
 * not to reintroduce it by quoting his own input back at him.
 *
 * The same rule governs the read: `PAScheduledTariff.effective_date` is a CIVIL day and
 * `effective_at` is the same moment as an instant. The instant is never printed — a civil day
 * formatted by the browser in its own zone renders as the previous day for any reader west of
 * a +03 operator, which is the correction the blocking wire took the same day.
 *
 * # ⚠ THREE SERVER VERDICTS, NONE OF THEM RE-DERIVED HERE
 *
 *   `change_available` — may the form be offered at all. It is a boundary being known AND at
 *     least one transition being flagged, and the tempting client copy
 *     (`available.length > 0`) is a second statement of a rule that can drift from the one
 *     that enforces: the server may refuse for a reason this screen has never heard of, and
 *     then «Сменить» is a 409 the subscriber cannot act on.
 *   `boundary_known` — whether the charging engine could name the next moment money moves.
 *     FALSE means NO FORM AT ALL and an explanation: such an Account has no armed charge
 *     cadence (never activated, or one not armed), so there is no date at which a change
 *     would take effect, and naming one anyway is the defect. He must telephone, and the
 *     screen says so rather than showing a form that could only fail.
 *   `earliest_date` / `latest_date` — the window's own ends, civil dates in the OPERATOR's
 *     billing zone. The browser cannot compute either: it does not know
 *     `general.billing_timezone`, and around every local midnight a browser-derived «today»
 *     is simply a different day. They are the date field's `min` and `max`.
 *
 * # ⚠ WHAT THE FEE FIGURE IS, AND WHAT IT IS NOT
 *
 * `subscription_fee` is MINOR UNITS at the date the row was resolved for — «now» for the
 * current tariff, **THE BOUNDARY** for an offered one — so the figure he compares is the
 * figure his first cycle on the new tariff will be charged. It carries NO recurring-method
 * discount: §3.11's reduction is applied inside the charging engine, and a screen subtracting
 * a percentage would quote a price the engine never charges. `money-arithmetic.test.ts` makes
 * the attempt unwritable.
 *
 * # WHAT IS DELIBERATELY NOT ON THIS SCREEN
 *
 *   - **AN «EDIT» ON A SCHEDULED CHANGE.** Appendix E §2.3 says «edited or cancelled» and the
 *     server implements the cancel only — an edit is a cancel plus a schedule, which the two
 *     routes compose with no half-applied state in between. So the screen offers the pair and
 *     not a third control nothing implements.
 *   - **A MONEY GATE.** `ScheduleTariff` never reads a balance: a subscriber in arrears may
 *     schedule a switch to the most expensive tariff on the list, which is the staff path's
 *     behaviour too. A portal-only arrears refusal would be a rule the operator never
 *     configured and a second answer about affordability.
 *   - **«ИСТОРИЯ ТАРИФНЫХ ПЛАНОВ» (§7e).** Nothing on this wire carries who changed the
 *     tariff and when — `ListTariffAssignments` is not on the portal listener — so the heading
 *     would be a section with no rows. §9 «Расход средств» is where the charges under each
 *     tariff are already visible.
 *   - **«ПОДБОР ТАРИФОВ» (§7a's intro link).** There is no tariff-selection wizard anywhere in
 *     this system, and a link to one would be the dead control this app removes rather than
 *     draws.
 */
export function TariffScreen() {
  return (
    <MoneyPage titleKey="tariff.title" leadKey="tariff.lead">
      {(contract) => (
        <AccountScope
          contractID={contract.id}
          labelKey="tariff.accountLabel"
          emptyKey="tariff.noAccounts"
          selectID="pa-tariff-account"
        >
          {(account) => <TariffBody contract={contract} account={account} />}
        </AccountScope>
      )}
    </MoneyPage>
  );
}

function TariffBody({ contract, account }: { contract: PAContract; account: PAAccount }) {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PATariff>(
    () => getTariff(contract.id, account.account_id),
    [contract.id, account.account_id],
  );

  const [choosing, setChoosing] = useState<PATariffOption | null>(null);
  const [dateText, setDateText] = useState('');
  const [cancelling, setCancelling] = useState<PAScheduledTariff | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');

  return (
    <QueryFrame query={query} reload={reload}>
      {(data) => {
        const openForm = (option: PATariffOption) => {
          setError(null);
          setNotice('');
          // ⚠ PREFILLED WITH THE BOUNDARY AND NOT WITH «TODAY». The boundary IS the day the
          // change happens, so the prefill is the truthful default; a browser-derived today
          // would be refused (`earliest_date` is tomorrow) and would also be the wrong day
          // for every subscriber whose device is not in the operator's zone.
          setDateText(data.effective_date);
          setChoosing(option);
        };

        const submit = async () => {
          if (!choosing) return;
          setBusy(true);
          setError(null);
          try {
            const written = await scheduleTariffChange(
              contract.id,
              account.account_id,
              choosing.tariff_id,
              dateText.trim(),
            );
            // ⚠ THE DATE THE SERVER WROTE, from the response — never `dateText`. See the file
            // header: a date inside the window is snapped forward, so the two differ exactly
            // when the subscriber most needs to be told which one is real.
            setNotice(
              t('tariff.scheduled', {
                name: choosing.name,
                date: formatCivilDate(written.effective_date),
              }),
            );
            setChoosing(null);
          } catch (err) {
            setError(err);
            setChoosing(null);
          } finally {
            setBusy(false);
            // The list now carries the new pending row, and the boundary may itself have
            // moved — so it is re-read rather than patched. The write's answer carries the
            // tariff's ID and nothing else about it.
            reload();
          }
        };

        const cancel = async () => {
          if (!cancelling) return;
          setBusy(true);
          setError(null);
          try {
            await cancelScheduledTariff(contract.id, account.account_id, cancelling.assignment_id);
            setNotice(t('tariff.cancelled'));
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
            setCancelling(null);
            reload();
          }
        };

        return (
          <>
            {notice ? (
              <Alert type="success" showIcon message={notice} style={{ marginBottom: 16 }} />
            ) : null}
            {error ? (
              <Alert
                type="error"
                showIcon
                message={tariffErrorText(error, t)}
                style={{ marginBottom: 16 }}
              />
            ) : null}

            <Card
              className="pa-block"
              title={t('tariff.currentTitle')}
              extra={<span className="mono">{data.login}</span>}
            >
              {data.current ? (
                <FactList facts={optionFacts(data.current, contract.currency, t)} />
              ) : (
                <Alert type="info" showIcon message={t('tariff.noCurrent')} />
              )}
            </Card>

            {data.scheduled.length > 0 ? (
              <Card className="pa-block" title={t('tariff.scheduledTitle')}>
                {data.scheduled.map((plan) => (
                  <div className="pa-plan" key={plan.assignment_id}>
                    <span className="pa-plan-what">
                      {/* ⚠ `effective_date`, THE CIVIL DAY — never `effective_at`. */}
                      {t('tariff.scheduledRow', {
                        name: plan.tariff.name || t('tariff.unnamedTariff'),
                        date: formatCivilDate(plan.effective_date),
                      })}
                      {/* ⚠ A STAFF-PLANNED ROW MAY NOT SIT ON A BOUNDARY. This surface writes
                          nothing but boundary dates, so only an operator's row can carry a day
                          on which the money does not move — and `scheduled_by_staff` is the
                          only way the screen can know. Saying so is cheaper than the support
                          call «мне сменили тариф 15-го, а списали по старому». */}
                      {plan.scheduled_by_staff ? (
                        <span className="pa-field-hint">{t('tariff.plannedByStaff')}</span>
                      ) : null}
                    </span>
                    <Button size="large" onClick={() => setCancelling(plan)}>
                      {t('tariff.removeFromSchedule')}
                    </Button>
                  </div>
                ))}
              </Card>
            ) : null}

            <Card className="pa-block" title={t('tariff.availableTitle')}>
              {/* ⚠ THE THREE OUTCOMES, AND THE FORM IS DRAWN FOR EXACTLY ONE OF THEM. */}
              {data.pending_removal ? (
                // ⚠ FIRST, before the boundary: the operator's removal outranks every other
                // reason (T-10), and it names its own date — the civil day, never the instant.
                <Alert
                  type="warning"
                  showIcon
                  message={t('tariff.removalPending', {
                    date: formatCivilDate(data.pending_removal.effective_date),
                  })}
                />
              ) : !data.boundary_known ? (
                <Alert type="info" showIcon message={t('tariff.boundaryUnknown')} />
              ) : !data.change_available ? (
                <Alert type="info" showIcon message={t('tariff.noTransitions')} />
              ) : (
                <>
                  <p className="pa-page-lead">
                    {t('tariff.effectiveNote', { date: formatCivilDate(data.effective_date) })}
                  </p>
                  <DataTable
                    columns={availableColumns(contract.currency, t, openForm)}
                    rows={data.available}
                    rowKey={(o) => o.tariff_id}
                    emptyKey="tariff.availableEmpty"
                    detail={(o) => (o.description ? <span>{o.description}</span> : null)}
                  />
                </>
              )}
            </Card>

            <Modal
              open={choosing !== null}
              title={t('tariff.changeTitle')}
              okText={t('tariff.change')}
              cancelText={t('tariff.cancel')}
              okButtonProps={{ loading: busy }}
              onOk={() => void submit()}
              onCancel={() => setChoosing(null)}
            >
              {choosing ? (
                <ChangeForm
                  option={choosing}
                  login={data.login}
                  currency={contract.currency}
                  state={data}
                  dateText={dateText}
                  onDateText={setDateText}
                />
              ) : null}
            </Modal>

            <Modal
              open={cancelling !== null}
              title={t('tariff.removeTitle')}
              okText={t('tariff.removeConfirm')}
              cancelText={t('tariff.removeBack')}
              okButtonProps={{ danger: true, loading: busy }}
              onOk={() => void cancel()}
              onCancel={() => setCancelling(null)}
            >
              <p>{t('tariff.removeText')}</p>
            </Modal>
          </>
        );
      }}
    </QueryFrame>
  );
}

/** §7a's three facts about the tariff in force, plus which cadence charges it. */
function optionFacts(
  option: PATariffOption,
  currency: string,
  t: TFunction,
): Array<{ key: string; label: string; value: ReactNode; hint?: ReactNode }> {
  const facts: Array<{ key: string; label: string; value: ReactNode; hint?: ReactNode }> = [
    {
      key: 'name',
      label: t('tariff.nameLabel'),
      value: option.name || t('tariff.unnamedTariff'),
      hint: option.description || billingModelLabel(option.billing_model, t) || undefined,
    },
    {
      key: 'fee',
      label: t('tariff.feeLabel'),
      value: <Money minor={option.subscription_fee} currency={currency} />,
      hint: t('tariff.feeHint'),
    },
  ];
  if (option.bandwidth_kbps) {
    facts.push({
      key: 'speed',
      label: t('tariff.speedLabel'),
      // ⚠ THE UNIT THE SERVER SENT (Кбит/с), for the reason the dashboard's own hint records:
      // §3's example prints «12,197 Мбит/с» for what is plainly 12 197 Кбит/с, i.e. the legacy
      // divided by 1000 and relabelled. Repeating that would render a 102 400-Кбит/с tariff as
      // «102,4 Мбит/с» instead of 100, and the tariff's NAME already carries the marketing
      // figure. The key is the dashboard's own, so the two screens cannot word it differently.
      value: t('dashboard.speed', { kbps: option.bandwidth_kbps }),
    });
  }
  return facts;
}

function availableColumns(
  currency: string,
  t: TFunction,
  onChoose: (option: PATariffOption) => void,
): ReadonlyArray<Column<PATariffOption>> {
  return [
    {
      key: 'name',
      labelKey: 'tariff.nameLabel',
      track: 'minmax(0, 2fr)',
      render: (o) => o.name || t('tariff.unnamedTariff'),
    },
    {
      key: 'fee',
      labelKey: 'tariff.feeLabel',
      className: 'pa-td-right',
      render: (o) => <Money minor={o.subscription_fee} currency={currency} />,
    },
    {
      key: 'speed',
      labelKey: 'tariff.speedLabel',
      render: (o) =>
        o.bandwidth_kbps ? t('dashboard.speed', { kbps: o.bandwidth_kbps }) : t('tariff.speedNone'),
    },
    {
      key: 'action',
      labelKey: 'tariff.columnAction',
      render: (o) => (
        <Button type="primary" size="large" onClick={() => onChoose(o)}>
          {t('tariff.change')}
        </Button>
      ),
    },
  ];
}

/**
 * §7c — the date form. Two displays, one optional date, and one sentence about what happens.
 *
 * ⚠ THE FIELD IS OPTIONAL AND SAYS SO. The server reads an empty value as «at the next
 * boundary», which is also what every date inside the window resolves to — so leaving it alone
 * is the ordinary case, and the form must not demand a value it will then override.
 */
function ChangeForm({
  option,
  login,
  currency,
  state,
  dateText,
  onDateText,
}: {
  option: PATariffOption;
  login: string;
  currency: string;
  state: PATariff;
  dateText: string;
  onDateText: (next: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <span className="pa-field-label">{t('tariff.accountLabel')}</span>
      <p className="pa-contact-value">
        <span className="mono">{login}</span>
      </p>

      <span className="pa-field-label">{t('tariff.chosenLabel')}</span>
      <p className="pa-contact-value">
        {option.name || t('tariff.unnamedTariff')}
        <Money minor={option.subscription_fee} currency={currency} />
      </p>

      <label className="pa-field-label" htmlFor="pa-tariff-date">
        {t('tariff.dateLabel')}
      </label>
      <Input
        id="pa-tariff-date"
        type="date"
        size="large"
        value={dateText}
        // ⚠ BOTH BOUNDS ARE THE SERVER'S CIVIL DATES. `min` is tomorrow (a change dated today
        // would re-price the running cycle, which T2(a) forbids) and `max` is the boundary
        // itself, because only ONE boundary is knowable — projecting the second would mean
        // re-deriving a cadence that moves with every successful debit under Model C.
        min={state.earliest_date}
        max={state.latest_date}
        onChange={(e) => onDateText(e.target.value)}
      />
      {/* ⚠ THE SENTENCE THAT MAKES THE SNAP HONEST BEFORE HE PRESSES. Whatever day he types,
          the change happens at the boundary — so the form says which day that is, and the
          success notice repeats the date the server actually wrote. */}
      <p className="pa-field-hint">
        {t('tariff.dateHint', { date: formatCivilDate(state.effective_date) })}
      </p>
      <Alert type="info" showIcon message={t('tariff.changeNote')} style={{ marginTop: 12 }} />
    </>
  );
}

/**
 * tariffErrorText is `errorText` plus the one refusal whose sentence needs a DATE the server's
 * static catalogue cannot carry: `pa.tariff_removal_pending` (fixes8 T-10) names the civil day
 * in `details.effective_date`.
 */
function tariffErrorText(err: unknown, t: TFunction): string {
  if (err instanceof ApiError && err.code === 'pa.tariff_removal_pending') {
    const d = err.details as { effective_date?: unknown } | undefined;
    if (d && typeof d.effective_date === 'string' && d.effective_date !== '') {
      return t('tariff.removalPending', { date: formatCivilDate(d.effective_date) });
    }
  }
  return errorText(err, t);
}
